/**
 * Compare — the statement with columns you choose.
 *
 * The working version is one chip. Add last year, the budget, last year's
 * budget or any other version of this year as further chips; click a chip
 * to make it the baseline; toggle Δ columns against that baseline; read it
 * as the two-section P&L or the GOP statement. All figures are totals over
 * the selected month range (one month up to the full year). Other versions are priced by the same calculator the Workspace
 * uses, so a version compares as it would display.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ArrowUpRight, ChevronDown, ChevronRight, Download, Maximize2, Minimize2, Plus, TrendingUp, X } from 'lucide-react';
import type { ForecastVersion } from '../../types';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { useForecastData } from '../../hooks/useForecastData';
import { useUsaliMetadata } from '../../hooks/useUsaliMetadata';
import { calculateForecast } from '../../api';
import { classifyDepartmentSections, type GridLayout } from '../../utils/usaliLayout';
import { formatCurrency } from '../../utils/formatters';
import { EmptyState } from '../shared/EmptyState';
import { TableSkeleton } from '../shared/Skeleton';
import { RibbonPortal } from '../layout/RibbonSlot';
// @ts-ignore - JavaScript module without types
import { downloadCsv } from '../../../../pages/reports/reportPageUtils';
import {
  buildCompareRows,
  MONTH_KEYS,
  visibleRows,
  compareToCsv,
  isFavourable,
  variance,
  type CompareRow,
  type CompareSeries,
  type SeriesId,
  type VersionTotals,
} from './compareData';
import { flashRow } from '../../utils/flashRow';
import { GLDetailModal } from '../workspace/GLDetailModal';
import { compareScopeKey, getCompareReturn, loadCompareView, saveCompareView, setCompareReturn } from './compareSession';
import { MonthRangeRail, monthRangeLabel, type MonthRange } from './MonthRangeRail';

interface ComparePageProps {
  versions: ForecastVersion[];
  currency?: string | null;
}

const SWATCH = {
  current: '#FF7A00',
  ly: '#72727F',
  budget: '#0BA5EC',
  lyBudget: '#A0A0AE',
  version: ['#12B76A', '#B35500', '#7A5AF8', '#E04F8A', '#0E9384'],
};

function money(value: number, currency?: string | null): string {
  const s = formatCurrency(Math.abs(value), true, currency);
  return value < 0 ? `(${s})` : s;
}
function signed(value: number, currency?: string | null): string {
  return `${value >= 0 ? '+' : '−'}${formatCurrency(Math.abs(value), true, currency)}`;
}

export function ComparePage({ versions, currency }: ComparePageProps) {
  const { state, setVersion, setActiveTab, setActiveMonth, setPendingAccountId } = useForecastV3();
  const { orgId, locationId, year, currentVersion } = state;
  const { monthVersions } = useMonthlyVersions();

  // The view survives a jump to the Workspace and back (see compareSession).
  const scope = compareScopeKey(orgId, locationId, year);
  const saved = useRef(loadCompareView(scope)).current;
  const [active, setActive] = useState<SeriesId[]>(saved?.active ?? ['current', 'ly', 'budget']);
  const [baseId, setBaseId] = useState<SeriesId>(saved?.baseId ?? 'current');
  const [showVariance, setShowVariance] = useState(saved?.showVariance ?? true);
  const [layout, setLayout] = useState<GridLayout>(() => {
    try { return localStorage.getItem('fv3:gridLayout') === 'gop' ? 'gop' : 'pnl'; } catch { return 'pnl'; }
  });
  // Closed months read as actuals on forecast columns unless the viewer turns it off.
  const [blendActuals, setBlendActuals] = useState(saved?.blendActuals ?? true);
  const [range, setRange] = useState<MonthRange>(saved?.range ?? [0, 11]);
  const periodIdx = useMemo(() => Array.from({ length: range[1] - range[0] + 1 }, (_, i) => range[0] + i), [range]);
  const periodLabel = monthRangeLabel(range, year);
  // Drilldown: bands start open, departments start closed (as in the Workspace).
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set(saved?.collapsedBands));
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set(saved?.expandedDepts));
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // Tracked on scroll: the grid's ref is already detached when the unmount snapshot runs.
  const scrollTopRef = useRef(0);
  // Pinned at the moment of a jump: the grid empties (and clamps to 0) on the way out.
  const jumpScrollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const lockedMonths = useMemo(
    () => currentVersion?.lockedMonths ?? monthVersions.filter((m) => m.status === 'locked').map((m) => m.month),
    [currentVersion?.lockedMonths, monthVersions]
  );

  const { accounts, isLoading, error, closedMonthKeys } = useForecastData({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    year,
    versionId: currentVersion?.id ?? null,
    requireVersion: true,
    lockedMonths,
    enabled: !!orgId && !!locationId,
    roomStatSource: currentVersion?.roomStatSource ?? 'pms',
    actualRollInEnabled: currentVersion?.actualRollInEnabled ?? true,
  });

  const { departments, revenueBasisLinks, deptOrder } = useUsaliMetadata(orgId, locationId);
  const sectionByDept = useMemo(
    () => classifyDepartmentSections({ accounts, departments, revenueBasisLinks, locationId: locationId ?? 0 }),
    [accounts, departments, revenueBasisLinks, locationId]
  );

  // ---- actuals blend ----------------------------------------------------
  // Forecast versions only: a budget shows budget values in every month.
  const isBudgetVersion = currentVersion?.type === 'Budget';
  const closedIdx = useMemo(
    () => MONTH_KEYS.map((k, i) => (closedMonthKeys.includes(k) ? i : -1)).filter((i) => i >= 0),
    [closedMonthKeys]
  );
  const closedInPeriod = useMemo(() => periodIdx.filter((i) => closedIdx.includes(i)), [periodIdx, closedIdx]);
  const canBlend = !isBudgetVersion && closedIdx.length > 0;
  const actualMonths = useMemo(
    () => new Set(canBlend && blendActuals ? closedIdx.map((i) => MONTH_KEYS[i]) : []),
    [canBlend, blendActuals, closedIdx]
  );
  /** Caption for a forecast column: what the closed months in view are made of. */
  const blendCaption = (isForecast: boolean): string => {
    if (!isForecast || !canBlend || closedInPeriod.length === 0) return '';
    if (!blendActuals) return ' · FORECAST ONLY';
    const span = closedInPeriod.length === 1
      ? MONTH_KEYS[closedInPeriod[0]].toUpperCase()
      : `${MONTH_KEYS[closedInPeriod[0]]}–${MONTH_KEYS[closedInPeriod[closedInPeriod.length - 1]]}`.toUpperCase();
    return closedInPeriod.length === periodIdx.length ? ` · ALL ACTUALS (${span})` : ` · ACTUALS ${span}`;
  };

  // ---- series catalogue --------------------------------------------------
  const otherVersions = useMemo(
    () => versions.filter((v) => v.id !== currentVersion?.id && v.year === year),
    [versions, currentVersion?.id, year]
  );
  const catalogue = useMemo<CompareSeries[]>(() => {
    const isBudget = currentVersion?.type === 'Budget';
    const list: CompareSeries[] = [
      {
        id: 'current',
        label: currentVersion?.name || 'Working version',
        short: isBudget ? 'Budget' : 'Fcst',
        sub: isBudget ? 'BUDGET' : 'FORECAST',
        swatch: SWATCH.current,
        note: 'Working version',
      },
      { id: 'ly', label: 'Same period LY', short: 'LY', sub: `ACTUAL ${year - 1}`, swatch: SWATCH.ly, note: `${year - 1} actuals, same months` },
      { id: 'budget', label: `${year} Budget`, short: 'Budget', sub: 'BUDGET', swatch: SWATCH.budget, note: 'Approved budget' },
      { id: 'lyBudget', label: `${year - 1} Budget`, short: 'LY Budget', sub: 'LY BUDGET', swatch: SWATCH.lyBudget, note: 'Prior-year approved budget' },
    ];
    otherVersions.forEach((v, i) => {
      list.push({
        id: `v:${v.id}`,
        label: v.name,
        short: v.name.length > 14 ? `${v.name.slice(0, 13)}…` : v.name,
        sub: v.type === 'Budget' ? 'BUDGET' : 'FORECAST',
        swatch: SWATCH.version[i % SWATCH.version.length],
        note: `Other ${v.type === 'Budget' ? 'budget' : 'forecast'} version`,
      });
    });
    return list;
  }, [currentVersion?.name, currentVersion?.type, year, otherVersions]);

  const series = useMemo(
    () => active.map((id) => catalogue.find((s) => s.id === id)).filter((s): s is CompareSeries => !!s),
    [active, catalogue]
  );
  const base = series.find((s) => s.id === baseId) || series[0];

  // ---- other versions: priced by the Workspace's calculator, period totals ----
  const accountIds = useMemo(() => accounts.map((a) => a.chartOfAccountId), [accounts]);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`), [year]);
  const versionIds = active.filter((id) => id.startsWith('v:')).map((id) => Number(id.slice(2)));
  const versionQueries = useQueries({
    queries: versionIds.map((versionId) => ({
      queryKey: ['forecast-v3', 'compare-version', { orgId, locationId, versionId, year, n: accountIds.length }],
      queryFn: () => calculateForecast({
        orgId: orgId!,
        locationId: locationId!,
        versionId,
        chartOfAccountIds: accountIds,
        months,
        roomStatSource: versions.find((v) => v.id === versionId)?.roomStatSource ?? 'pms',
      }),
      enabled: !!orgId && !!locationId && accountIds.length > 0,
      staleTime: 60000,
    })),
  });
  const periodMonths = useMemo(() => new Set(periodIdx.map((i) => months[i])), [periodIdx, months]);
  const expenseIds = useMemo(() => new Set(accounts.filter((a) => a.accountType !== 'Revenue').map((a) => a.chartOfAccountId)), [accounts]);
  const versionTotals = useMemo(() => {
    const map = new Map<string, VersionTotals>();
    versionIds.forEach((versionId, i) => {
      const totals: VersionTotals = new Map();
      const versionIsForecast = versions.find((v) => v.id === versionId)?.type !== 'Budget';
      (versionQueries[i]?.data?.results || []).forEach((r) => {
        if (!periodMonths.has(r.month)) return;
        const monthKey = MONTH_KEYS[Number(r.month.slice(5, 7)) - 1];
        if (versionIsForecast && actualMonths.has(monthKey)) {
          const actual = r.actualAmount || 0;
          totals.set(r.chartOfAccountId, (totals.get(r.chartOfAccountId) || 0) + (expenseIds.has(r.chartOfAccountId) ? actual : -actual));
          return;
        }
        totals.set(r.chartOfAccountId, (totals.get(r.chartOfAccountId) || 0) + (r.calculatedAmount || 0));
      });
      map.set(`v:${versionId}`, totals);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionIds.join(','), versionQueries.map((q) => q.dataUpdatedAt).join(','), periodMonths, actualMonths, expenseIds]);
  const versionsLoading = versionQueries.some((q) => q.isLoading);

  const rows = useMemo(
    () => buildCompareRows({ accounts, layout, series, versionTotals, sectionByDept, deptOrder, months: periodIdx, actualMonths: isBudgetVersion ? undefined : actualMonths }),
    [accounts, layout, series, versionTotals, sectionByDept, deptOrder, periodIdx, actualMonths, isBudgetVersion]
  );

  const shownRows = useMemo(() => visibleRows(rows, collapsedBands, expandedDepts), [rows, collapsedBands, expandedDepts]);
  const deptKeys = useMemo(() => rows.filter((r) => r.kind === 'leaf').map((r) => r.key), [rows]);
  const allExpanded = deptKeys.length > 0 && collapsedBands.size === 0 && deptKeys.every((k) => expandedDepts.has(k));
  const toggleRow = (r: CompareRow) => {
    const flip = (set: Set<string>) => { const next = new Set(set); if (next.has(r.key)) next.delete(r.key); else next.add(r.key); return next; };
    if (r.kind === 'head') setCollapsedBands(flip);
    else if (r.kind === 'leaf') setExpandedDepts(flip);
  };
  const toggleAll = () => {
    setCollapsedBands(new Set());
    setExpandedDepts(allExpanded ? new Set() : new Set(deptKeys));
  };

  // ---- session memory ---------------------------------------------------
  const snapshot = () => saveCompareView(scope, {
    active, baseId, showVariance, blendActuals, range,
    collapsedBands: Array.from(collapsedBands), expandedDepts: Array.from(expandedDepts),
    scrollTop: jumpScrollRef.current ?? scrollTopRef.current,
  });
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  useEffect(() => () => snapshotRef.current(), []);

  // Back from the Workspace: restore the scroll and flash the row jumped from.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || shownRows.length === 0) return;
    restored.current = true;
    const back = getCompareReturn();
    if (back) setCompareReturn(null);
    if (!saved) return;
    // The grid keeps growing while versions and departments land, so hold the
    // restore until it is tall enough to take the saved offset.
    let tries = 0;
    const apply = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const reachable = grid.scrollHeight - grid.clientHeight >= saved.scrollTop;
      if (!reachable && ++tries < 30) { window.setTimeout(apply, 100); return; }
      grid.scrollTop = saved.scrollTop;
      if (!back) return;
      const row = grid.querySelector(`[data-row-key="${CSS.escape(back.rowKey)}"]`);
      row?.scrollIntoView({ block: 'nearest' });
      flashRow(row);
    };
    requestAnimationFrame(apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownRows.length]);

  // GL detail behind an actuals figure, over the months in view.
  const [glDetail, setGlDetail] = useState<{ accountId: number; columnType: 'Actual' | 'LY'; month: number; endMonth: number } | null>(null);
  const glAccount = glDetail ? accounts.find((a) => a.chartOfAccountId === glDetail.accountId) : undefined;
  // The working column is pure actuals when every month in view is closed and blended.
  const currentIsAllActuals = canBlend && blendActuals && closedInPeriod.length === periodIdx.length;

  /** Where an account-row figure links: forecast/budget versions → Workspace, actuals → GL detail. */
  const cellLink = (accountId: number, s: CompareSeries): { onClick: () => void; title: string } | null => {
    if (s.id === 'ly') {
      return { onClick: () => setGlDetail({ accountId, columnType: 'LY', month: range[0] + 1, endMonth: range[1] + 1 }), title: `GL detail · ${year - 1} actuals` };
    }
    if (s.id === 'current' && currentIsAllActuals) {
      return { onClick: () => setGlDetail({ accountId, columnType: 'Actual', month: range[0] + 1, endMonth: range[1] + 1 }), title: `GL detail · ${year} actuals` };
    }
    if (s.id === 'current' || s.id.startsWith('v:')) {
      return { onClick: () => openInWorkspace(accountId, s.id), title: `Open this account in the Workspace · ${s.label}` };
    }
    return null; // the approved budget has no version to open
  };

  // Jump to an account in the Workspace — on the working version, or on the
  // version whose column was clicked. A single-month period carries over.
  const openInWorkspace = (accountId: number, seriesId: SeriesId = 'current') => {
    const row = rows.find((r) => r.accountId === accountId);
    jumpScrollRef.current = gridRef.current?.scrollTop ?? scrollTopRef.current;
    setCompareReturn({ rowKey: row?.key ?? '', accountLabel: row?.name ?? 'account', versionId: currentVersion?.id ?? null });
    if (seriesId.startsWith('v:')) {
      const target = versions.find((v) => v.id === Number(seriesId.slice(2)));
      if (target) setVersion(target);
    }
    if (range[0] === range[1]) setActiveMonth(range[0] + 1);
    setPendingAccountId(accountId);
    setActiveTab('workspace');
  };

  // ---- chip actions ------------------------------------------------------
  const addSeries = (id: SeriesId) => { setActive((a) => (a.includes(id) ? a : [...a, id])); setMenuOpen(false); };
  const removeSeries = (id: SeriesId) => {
    setActive((a) => {
      const next = a.filter((k) => k !== id);
      if (baseId === id && next[0]) setBaseId(next[0]);
      return next;
    });
  };
  const changeLayout = (next: GridLayout) => {
    setLayout(next);
    try { localStorage.setItem('fv3:gridLayout', next); } catch { /* private mode */ }
  };

  const handleExport = useCallback(() => {
    if (!base) return;
    const name = `compare-${(currentVersion?.name || 'version').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${periodLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    downloadCsv(name, compareToCsv(shownRows, series, base.id, showVariance));
  }, [shownRows, series, base, showVariance, currentVersion?.name, periodLabel]);

  const segBtn = (on: boolean) =>
    `fv3-seg-btn px-2.5 py-1 text-[12.5px] rounded-md transition-colors ${on ? 'fv3-seg-on font-semibold' : 'font-medium'}`;

  const ribbon = (
    <div className="flex items-center gap-2 px-3 flex-none">
      <button
        type="button"
        onClick={() => setShowVariance((v) => !v)}
        className="flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-[12.5px] font-medium whitespace-nowrap flex-none"
        style={showVariance
          ? { background: 'var(--lodge-onyx-900)', border: '1px solid var(--lodge-onyx-900)', color: 'var(--lodge-white)' }
          : { background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-200)', color: 'var(--lodge-onyx-600)' }}
        aria-pressed={showVariance}
        title={`Variance columns against ${base?.label ?? 'the baseline'}`}
      >
        <TrendingUp size={14} />
        Variance
      </button>
      <div className="fv3-seg inline-flex items-center rounded-lg p-0.5 flex-none" style={{ background: 'var(--lodge-onyx-50)' }} role="group" aria-label="Statement layout">
        <button type="button" onClick={() => changeLayout('pnl')} className={segBtn(layout === 'pnl')}>P&L</button>
        <button type="button" onClick={() => changeLayout('gop')} className={segBtn(layout === 'gop')}>GOP</button>
      </div>
      <button
        type="button"
        onClick={handleExport}
        disabled={rows.length === 0}
        className="flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-[12.5px] font-medium transition-colors disabled:opacity-40"
        style={{ color: 'var(--lodge-onyx-700)', border: '1px solid var(--lodge-onyx-200)' }}
        title="Export the visible columns as CSV"
      >
        <Download size={14} />
        Export
      </button>
    </div>
  );

  if (!currentVersion) {
    return (
      <div className="flex-1 p-6 overflow-auto">
        <EmptyState type="compare" title="No version selected" description="Pick a version in the ribbon, then add the columns to compare it against." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex-1 p-6 overflow-auto">
        <EmptyState type="compare" title="Could not load the comparison" description={(error as Error).message} />
      </div>
    );
  }

  return (
    <div className="fv3-compare flex-1 min-h-0 flex flex-col min-w-0 relative" style={{ background: 'var(--lodge-white)' }}>
      <RibbonPortal>{ribbon}</RibbonPortal>

      {/* Columns bar — chips only, so extra columns wrap onto further rows */}
      <div className="flex-none flex items-center gap-2 px-3.5 py-2.5 flex-wrap" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        <span className="text-[10.5px] font-semibold mr-0.5" style={{ letterSpacing: '0.8px', color: 'var(--lodge-onyx-400)' }}>COLUMNS</span>
        {series.map((s) => {
          const isBase = s.id === base?.id;
          return (
            <span
              key={s.id}
              className="flex items-center gap-1.5 h-[30px] pl-2.5 pr-1 rounded-full text-[12.5px] font-medium whitespace-nowrap flex-none"
              style={isBase
                ? { background: 'var(--lodge-orange-50)', border: '1px solid var(--lodge-orange-200)', color: 'var(--lodge-orange-600)' }
                : { background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-200)', color: 'var(--lodge-onyx-700)' }}
            >
              <button type="button" onClick={() => setBaseId(s.id)} className="flex items-center gap-1.5" title="Use as variance baseline">
                <span className="w-2 h-2 rounded-sm" style={{ background: s.swatch }} />
                {s.label}
                {isBase && <span className="text-[10px] font-semibold" style={{ letterSpacing: '0.5px', color: 'var(--lodge-onyx-500)' }}>BASE</span>}
              </button>
              {series.length > 1 && (
                <button type="button" onClick={() => removeSeries(s.id)} className="w-5 h-5 rounded-full flex items-center justify-center" style={{ color: 'var(--lodge-onyx-400)' }} aria-label={`Remove ${s.label}`}>
                  <X size={11} strokeWidth={2.4} />
                </button>
              )}
            </span>
          );
        })}

        <div ref={menuRef} className="relative flex-none">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-1.5 h-[30px] px-2.5 rounded-full text-[12.5px] font-medium whitespace-nowrap"
            style={{ border: '1px dashed var(--lodge-onyx-200)', color: 'var(--lodge-onyx-600)' }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Plus size={13} strokeWidth={2.2} />
            Add column
          </button>
          {menuOpen && (
            <div className="absolute left-0 top-full mt-1.5 w-[316px] rounded-xl overflow-hidden z-[100]" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-lg)' }} role="menu">
              <div className="px-3.5 py-2.5 text-[11px] font-semibold" style={{ letterSpacing: '0.7px', color: 'var(--lodge-onyx-400)', borderBottom: '1px solid var(--lodge-onyx-100)' }}>ADD A COMPARISON COLUMN</div>
              <div className="max-h-[300px] overflow-y-auto py-1">
                {catalogue.map((s) => {
                  const on = active.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="menuitem"
                      disabled={on}
                      onClick={() => addSeries(s.id)}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left hover:bg-slate-50 disabled:cursor-default"
                    >
                      <span className="w-2 h-2 rounded-sm flex-none" style={{ background: s.swatch }} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[12.5px] font-medium" style={{ color: on ? 'var(--lodge-onyx-400)' : 'var(--lodge-onyx-800)' }}>{s.label}</span>
                        <span className="block text-[11px]" style={{ color: 'var(--lodge-onyx-400)' }}>{s.note}</span>
                      </span>
                      {on && <span className="text-[11px] font-semibold" style={{ color: 'var(--lodge-onyx-300)' }}>ADDED</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Period bar */}
      <div className="flex-none flex items-center gap-3 px-3.5 py-2 flex-wrap" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        <span className="text-[10.5px] font-semibold" style={{ letterSpacing: '0.8px', color: 'var(--lodge-onyx-400)' }}>PERIOD</span>
        <MonthRangeRail value={range} onChange={setRange} closedMonths={closedIdx} />
        {canBlend && (
          <div className="fv3-seg inline-flex items-center rounded-lg p-0.5 flex-none ml-auto" style={{ background: 'var(--lodge-onyx-50)' }} role="group" aria-label="Actuals blend">
            <button type="button" onClick={() => setBlendActuals(true)} className={segBtn(blendActuals).replace('text-[12.5px]', 'text-xs')} title="Closed months (dotted on the rail) show actuals on forecast columns; open months show the forecast">
              Actuals + forecast
            </button>
            <button type="button" onClick={() => setBlendActuals(false)} className={segBtn(!blendActuals).replace('text-[12.5px]', 'text-xs')} title="Every month shows the forecast, including months already closed">
              Forecast only
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div ref={gridRef} onScroll={(e) => { scrollTopRef.current = e.currentTarget.scrollTop; }} className="flex-1 min-h-0 overflow-auto">
        {isLoading && rows.length === 0 ? (
          <div className="p-6"><TableSkeleton rows={10} /></div>
        ) : rows.length === 0 ? (
          <div className="p-6"><EmptyState type="compare" title="Nothing to compare yet" description="This version has no accounts with values." /></div>
        ) : (
          <table className="fv3-compare-grid text-[13px]" style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 text-left align-bottom px-3.5 pb-2 pt-3 text-[11px] font-semibold" style={{ letterSpacing: '0.8px', color: 'var(--lodge-onyx-400)', background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)', borderRight: '1px solid var(--lodge-onyx-100)', minWidth: 280, boxShadow: '2px 0 6px -4px rgba(15,15,17,.18)' }}>
                  <span className="flex items-center justify-between gap-2">
                    ACCOUNT
                    <button
                      type="button"
                      onClick={toggleAll}
                      disabled={deptKeys.length === 0}
                      className="flex items-center gap-1 h-[22px] px-1.5 rounded-md text-[11px] font-medium normal-case disabled:opacity-40 hover:bg-slate-50"
                      style={{ letterSpacing: 0, color: 'var(--lodge-onyx-500)' }}
                      title={allExpanded ? 'Collapse every department' : 'Expand every department to its accounts'}
                    >
                      {allExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                      {allExpanded ? 'Collapse all' : 'Expand all'}
                    </button>
                  </span>
                </th>
                {series.map((s) => {
                  const isBase = s.id === base?.id;
                  return (
                    <React.Fragment key={s.id}>
                      <th className="sticky top-0 z-20 text-right align-bottom px-3 pb-2 pt-3" style={{ minWidth: 158, background: isBase ? 'var(--lodge-orange-50)' : 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)', borderRight: '1px solid var(--lodge-onyx-100)' }}>
                        <div className="text-[11.5px] font-semibold leading-tight" style={{ color: isBase ? 'var(--lodge-orange-600)' : 'var(--lodge-onyx-800)' }}>{s.label}</div>
                        <div className="text-[9.5px] font-semibold" style={{ letterSpacing: '0.6px', color: 'var(--lodge-onyx-400)' }}>
                          {s.sub}{blendCaption(s.sub === 'FORECAST')}{s.id.startsWith('v:') && versionsLoading ? ' · loading' : ''}
                        </div>
                      </th>
                      {showVariance && !isBase && base && (
                        <th className="sticky top-0 z-20 text-right align-bottom px-3 pb-2 pt-3" style={{ minWidth: 146, background: 'var(--lodge-onyx-50)', borderBottom: '1px solid var(--lodge-onyx-100)', borderRight: '1px solid var(--lodge-onyx-100)' }}>
                          <div className="text-[11.5px] font-semibold leading-tight" style={{ color: 'var(--lodge-onyx-600)' }}>{base.short} vs {s.short}</div>
                          <div className="text-[9.5px] font-semibold" style={{ letterSpacing: '0.6px', color: 'var(--lodge-onyx-400)' }}>VARIANCE</div>
                        </th>
                      )}
                    </React.Fragment>
                  );
                })}
                <th className="sticky top-0 z-20" style={{ width: '100%', background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)' }} />
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => {
                const canToggle = r.kind === 'head' || r.kind === 'leaf';
                const isOpen = r.kind === 'head' ? !collapsedBands.has(r.key) : expandedDepts.has(r.key);
                const nameStyle = r.kind === 'total'
                  ? { fontSize: 13.5, fontWeight: 700, color: 'var(--lodge-onyx-900)' }
                  : r.kind === 'head'
                    ? { fontWeight: 600, color: 'var(--lodge-onyx-800)' }
                    : r.kind === 'account'
                      ? { fontSize: 12.5, color: 'var(--lodge-onyx-600)' }
                      : { color: 'var(--lodge-onyx-700)' };
                const rowBg = r.kind === 'total' ? 'var(--lodge-onyx-50)' : 'var(--lodge-white)';
                return (
                  <tr key={r.key} data-row-key={r.key} className="fv3-compare-row" style={{ borderBottom: '1px solid var(--lodge-onyx-50)', background: rowBg }}>
                    <td className="sticky left-0 z-10 px-3.5 whitespace-nowrap" style={{ height: r.kind === 'account' ? 32 : 38, background: rowBg, borderRight: '1px solid var(--lodge-onyx-100)', paddingLeft: 14 + r.indent, boxShadow: '2px 0 6px -4px rgba(15,15,17,.18)', ...nameStyle }}>
                      {canToggle ? (
                        <button type="button" onClick={() => toggleRow(r)} className="flex items-center gap-1.5 -ml-1" aria-expanded={isOpen} style={{ font: 'inherit', color: 'inherit' }}>
                          <span className="flex-none" style={{ color: 'var(--lodge-onyx-400)' }}>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                          {r.name}
                        </button>
                      ) : r.accountId ? (
                        <button
                          type="button"
                          onClick={() => openInWorkspace(r.accountId!)}
                          className="fv3-compare-jump group/jump flex items-center gap-1.5 max-w-full text-left hover:underline"
                          style={{ font: 'inherit', color: 'inherit' }}
                          title={`Open in the Workspace · ${currentVersion.name}`}
                        >
                          <span className="truncate">{r.name}</span>
                          <ArrowUpRight size={13} className="flex-none opacity-0 group-hover/jump:opacity-100 transition-opacity" style={{ color: 'var(--lodge-orange-600)' }} />
                        </button>
                      ) : r.name}
                    </td>
                    {series.map((s) => {
                      const isBase = s.id === base?.id;
                      const v = r.values[s.id] || 0;
                      const d = base ? variance(r, base.id, s.id) : 0;
                      const good = isFavourable(r, d);
                      const link = r.accountId ? cellLink(r.accountId, s) : null;
                      return (
                        <React.Fragment key={s.id}>
                          <td className="text-right px-3 font-mono tabular-nums" style={{ background: isBase ? 'var(--lodge-orange-50)' : undefined, borderRight: '1px solid var(--lodge-onyx-100)', fontWeight: r.kind === 'total' ? 700 : 400, color: r.kind === 'total' ? 'var(--lodge-onyx-900)' : 'var(--lodge-onyx-700)' }}>
                            {link ? (
                              <button
                                type="button"
                                onClick={link.onClick}
                                className="fv3-compare-link underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
                                style={{ font: 'inherit', color: 'inherit', textDecorationColor: 'var(--lodge-onyx-300)' }}
                                title={link.title}
                              >
                                {money(v, currency)}
                              </button>
                            ) : money(v, currency)}
                          </td>
                          {showVariance && !isBase && (
                            <td className="text-right px-3 font-mono tabular-nums font-medium" style={{ background: 'var(--lodge-onyx-50)', borderRight: '1px solid var(--lodge-onyx-100)', color: good ? 'var(--color-success-ink, #15803d)' : 'var(--color-error-ink, #b42318)' }}>
                              {signed(d, currency)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <td />
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {orgId && locationId && glDetail && glAccount && (
        <GLDetailModal
          isOpen
          onClose={() => setGlDetail(null)}
          orgId={orgId}
          locationId={locationId}
          chartOfAccountId={glDetail.accountId}
          accountName={glAccount.accountName}
          accountNumber={glAccount.accountNumber}
          year={year}
          month={glDetail.month}
          endMonth={glDetail.endMonth}
          columnType={glDetail.columnType}
          currency={currency}
        />
      )}
    </div>
  );
}

export default ComparePage;
